import { z } from 'zod'
import { WorkModel } from '@Entities/work/work'
import { isOverclaimAllowed } from '@App/helpers/is-overclaim-allowed'
import { OriginalPublisherModel } from '@Entities/work/original-publisher-model'
import { getCurrentMemberShares } from '@App/helpers/work-entities/utils/get-current-member-shares'

const minMaxCheck = (value: number | undefined) => value !== undefined && value >= 0.01 && value <= 100

const publisherRowsValidationSchema = (workDraftFromLocal: WorkModel) =>
    z
        .object({
            ['publisherName']: z.string(),
            ['publisherIPI']: z.string().optional(),
            ['publisherIPINumber']: z.string(),
            ['publisherShare']: z.string({ error: 'Enter a number between 0.01% - 100%' }).or(z.literal('')),
        })
        .refine(
            (value) => {
                if (value.publisherShare.trim() === '') {
                    return false
                }

                const allMatchingIndexes = workDraftFromLocal.originalPublishers.reduce((indexes, op, currentIndex) => {
                    if (op.ipiNumber === (value.publisherIPI ?? value.publisherIPINumber)) {
                        indexes.push(currentIndex)
                    }
                    return indexes
                }, [] as number[])

                return allMatchingIndexes.every((matchingIndex) => {
                    const hasAdmin =
                        workDraftFromLocal.originalPublishers[matchingIndex].administratorPublishers.length > 0
                    return hasAdmin ? true : minMaxCheck(Number(value.publisherShare))
                })
            },
            {
                path: ['publisherShare'],
                message: 'Enter a number between 0.01% - 100%',
            }
        )
        .optional()
        .array()
        .optional()

const administratorRowsValidationSchema = z
    .object({
        ['administrator']: z.string().optional(),
        ['administratorIPI']: z.string().optional(),
        ['administratorShare']: z.string({ error: 'Enter a number between 0.01% - 100%' }).or(z.literal('')),
    })
    .refine(
        (value) => {
            if (value.administratorShare.trim() === '') {
                return false
            }

            return minMaxCheck(Number(value.administratorShare))
        },
        {
            path: ['administratorShare'],
            message: 'Enter a number between 0.01% - 100%',
        }
    )
    .optional()
    .array()
    .optional()

const isUniqueAdminPublisherCombination = (allPublishers: OriginalPublisherModel[]): boolean => {
    const seenCombinations = new Set<string>()

    return allPublishers.every((publisher) => {
        const combinationKey: string = JSON.stringify({
            publisherIPI: publisher.ipiNumber,
            publisherName: publisher.publisherName,
            adminKey: publisher.administratorPublishers
                .map((admin) =>
                    JSON.stringify({
                        adminIPI: admin.ipiNumber,
                        adminName: admin.publisherName,
                    })
                )
                .sort(),
        })

        if (seenCombinations.has(combinationKey)) {
            return false
        }
        seenCombinations.add(combinationKey)
        return true
    })
}

const generatePublishersTableSchema = (workDraftLocal: WorkModel, currentRightsholderIpId: number) =>
    z
        .object({
            publisherRows: publisherRowsValidationSchema(workDraftLocal),
            administratorRows: administratorRowsValidationSchema.optional(),
            _rowError: z.string().optional(), // the "global" dummy field
        })
        .superRefine((refineValues, ctx) => {
            const { administratorRows, publisherRows } = refineValues
            const currentRightsholderSumShares = getCurrentMemberShares(workDraftLocal, currentRightsholderIpId)
            const totalSumOfShares = (publisherRows || [])
                .map((row) => row?.publisherShare)
                .concat(administratorRows?.map((row) => row?.administratorShare))
                .map((val) => val ?? '0')
                .map((val) => Number.parseFloat(val))
                .reduce((prev, curr) => prev + curr, 0)

            if (
                (totalSumOfShares > 100.06 && !isOverclaimAllowed(workDraftLocal)) ||
                currentRightsholderSumShares > 100.06
            ) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Total share percentage must not exceed 100.06%',
                    path: ['_rowError'],
                })
            }

            // shares with the same publisher + administrator combinations are not valid and should be combined/deleted by the user.
            if (!isUniqueAdminPublisherCombination(workDraftLocal.originalPublishers)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'The same Publisher and Administrator cannot appear on the same work more than once.',
                    path: ['_rowError'],
                })
            }
        })

export { generatePublishersTableSchema }
